/**
 * Paylasim API istemcisi.
 *
 * Sunucu iki ayri yuzey veriyor ve bu dosya ayrimi koruyor:
 *
 *   `/api/shares/*`         SAHIBIN yuzeyi - oturum + CSRF.
 *   `/api/shared/{slug}/*`  ZIYARETCININ yuzeyi - kimlik OPSIYONEL.
 */

import { api, apiJson, apiVoid, type ApiError } from './api'

export interface ShareInfo {
  id: string
  slug: string
  url: string
  conversation_id: string
  message_count: number
  include_attachments: boolean
  allow_fork: boolean
  require_login: boolean
  show_owner: boolean
  has_password: boolean
  expires_at: string | null
  revoked_at: string | null
  view_count: number
  created_at: string
}

export interface SharePreview {
  message_count: number
  attachments: Array<{ id: string; filename: string; byte_size: number; kind: string }>
}

export interface SharedAttachment {
  id: string
  filename: string
  mime: string
  byte_size: number
  kind: string
  width: number | null
  height: number | null
  url: string
  /** Sesli mesaj: kaydin suresi (saniye). */
  duration_seconds?: number | null
}

export interface SharedMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  think_text: string | null
  tool_cards: Record<string, unknown>[]
  model: string | null
  created_at: string
  attachments: SharedAttachment[]
  /** Sahip ekleri HARIC tuttu - arayuz yerlerinde gosterge koyuyor. */
  attachments_withheld: boolean
}

export interface SharedView {
  share: {
    slug: string
    allow_fork: boolean
    include_attachments: boolean
    created_at: string
    /**
     * Paylasanin adi - YALNIZCA sahibi istediyse dolu.
     *
     * Sunucu kapaliyken alani `null` gonderiyor, yani ad yanitin hicbir
     * yerinde gecmiyor: "arayuz gostermesin" yetmezdi, baglantiya sahip
     * herkes onu ham JSON'dan okuyabilirdi.
     */
    owner_name: string | null
  }
  conversation: { title: string }
  items: SharedMessage[]
}

export interface ShareOptions {
  /**
   * VARSAYILAN YOK ve bilincli: sunucu bu alani zorunlu tutuyor.
   *
   * Kullanici eklerin de paylasilacagini GORMEDEN paylasim
   * olusturamamali. Alanin zorunlu olmasi arayuzun uyariyi atlamasini
   * yapisal olarak engelliyor.
   */
  include_attachments: boolean
  up_to_message_id?: string | null
  from_message_id?: string | null
  allow_fork?: boolean
  require_login?: boolean
  /** Ziyaretci "bunu kim paylasti" cevabini gorsun mu - varsayilan hayir. */
  show_owner?: boolean
  password?: string | null
  expires_in_days?: number | null
}

// ------------------------------------------------------------- sahibin yuzeyi

export function listShares(): Promise<{ items: ShareInfo[] }> {
  return apiJson('/api/shares')
}

export function previewShare(
  conversationId: string,
  range: { from?: string | null; upTo?: string | null } = {},
): Promise<SharePreview> {
  const params = new URLSearchParams({ conversation_id: conversationId })
  if (range.from) params.set('from_message_id', range.from)
  if (range.upTo) params.set('up_to_message_id', range.upTo)
  return apiJson(`/api/shares/preview?${params.toString()}`)
}

export function createShare(
  conversationId: string,
  options: ShareOptions,
): Promise<ShareInfo> {
  return apiJson('/api/shares', {
    method: 'POST',
    body: JSON.stringify({ conversation_id: conversationId, ...options }),
  })
}

export function revokeShare(shareId: string): Promise<void> {
  return apiVoid(`/api/shares/${shareId}`, { method: 'DELETE' })
}

// ---------------------------------------------------------- ziyaretci yuzeyi

/**
 * Paylasilan gorunumu getirir.
 *
 * `api()` kullaniliyor (`apiJson` DEGIL) cunku hata DURUMUNU ayirt etmemiz
 * gerekiyor: 401 parola istiyor, 403 giris istiyor, 404 yok. `apiJson`
 * hepsini tek bir `ApiError`a cevirip kodu kaybediyor - ve o kod olmadan
 * kullaniciya ne yapmasi gerektigini SOYLEYEMEYIZ.
 */
export type SharedResult =
  | { kind: 'ok'; view: SharedView }
  | { kind: 'password' }
  | { kind: 'bad_password' }
  | { kind: 'login' }
  | { kind: 'missing' }

export async function fetchShared(slug: string, password?: string): Promise<SharedResult> {
  const query = password ? `?password=${encodeURIComponent(password)}` : ''
  const response = await api(`/api/shared/${encodeURIComponent(slug)}${query}`)

  if (response.ok) return { kind: 'ok', view: (await response.json()) as SharedView }

  const code = await errorCode(response)
  if (response.status === 401) return { kind: 'password' }
  if (response.status === 403 && code === 'bad_password') return { kind: 'bad_password' }
  if (response.status === 403) return { kind: 'login' }
  return { kind: 'missing' }
}

async function errorCode(response: Response): Promise<string> {
  try {
    const body = await response.json()
    return String(body?.error?.code ?? '')
  } catch {
    return ''
  }
}

export async function forkShared(
  slug: string,
  password?: string,
): Promise<{ conversation_id: string; title: string }> {
  const query = password ? `?password=${encodeURIComponent(password)}` : ''
  return apiJson(`/api/shared/${encodeURIComponent(slug)}/fork${query}`, { method: 'POST' })
}

export type { ApiError }
